import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Grid, Typography, ButtonGroup, Button, CircularProgress, IconButton, Box } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as Icon from '@material-ui/icons';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { useDispatch } from "react-redux";
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from 'react-router-dom';
import queryString from 'query-string';

import clsx from "clsx";
import _ from '../../lodash-wrapper';
import HomeSearchBar from "./homeSearchBar";
import HomeCarousel from "./homeCarousel";
import QuoteItem from '../shared/quote';
import { HOMESCREEN_PAGE_SIZE, HOME_SCREEN_INITIAL_SEARCH_OPTION_ID } from '../../constants';
import { searchCourseGroup } from "../../actions/courseAction";
import { HomePageSearchOption, CourseGroupOrdering } from "../../models/enums";
import { CourseCard } from '../shared/courseCard';
import MetaTags from '../shared/meta'
import StructuredData, {
    buildOrganizationStructuredData,
    buildWebsiteStructuredData,
} from '../shared/structuredData';
import { getCourseGroupUrl, getBlogDetailUrl, mergeArrayUnique } from '../../utils';
import { BLOG_LISTING_SEARCH_DEFAULT_PARAMS } from '../../constants';
import { listBlogs } from '../../actions/blogAction';

const useStyles = makeStyles((theme) => ({
    carouselContainer: {
        minHeight: '200px',
        overflowX:"hidden",
        margin: 0,
        padding: 0,
        paddingBottom: "20px",
        justifyContent: "center",
        alignItems: "center",
        [theme.breakpoints.down('xs')]: {width: '100vw', marginLeft:  -16},
    },
    carouselArrowAppearEnabled: {
        '&.Mui-disabled': {
            opacity: '1 !important',
            color: theme.palette.primary.main + ' !important',
        },
    },
    carouselGrid: {
        minHeight: '200px',
        margin: 0,
        padding: 0,
        display: "flex", 
        overflowX:"hidden",
        justifyContent: "center", 
        alignItems: "center",
    },
    headerHeight: {
        maxHeight: 300,
        alignItems: "center",
        justify: "center",
    },
    headerTitleField: {
        [theme.breakpoints.between('sm', 840)]: {marginTop: -30, marginBottom: 10},
    },
    headerTitle: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        letterSpacing: '0.1em',
        textAlign: 'center',
        fontSize: '1.4em',
        [theme.breakpoints.up('sm')]: {fontSize: '1.7em'},
        [theme.breakpoints.up('md')]: {fontSize: '2em'},
    },
    searchAndSelect: {
        textAlign: 'center',
        width: '100%',
    },
    searchBar: {
        textAlign: 'center',
        marginTop: -10,
    },
    selectButtonGroup: {
        textAlign: 'center',
        marginBottom: 20,
    },
    selectButton: {
        fontSize: '1.25em',
        [theme.breakpoints.down('sm')]: {fontSize: '1.19em'},
        [theme.breakpoints.down(840)]: {fontSize: '1.2em'},
        [theme.breakpoints.down('xs')]: {fontSize: '1.15em'},
        color: theme.palette.primary.main,
    },
    selectedButton: {
        fontSize: '1.25em',
        [theme.breakpoints.down('sm')]: {fontSize: '1.2em'},
        [theme.breakpoints.down('xs')]: {fontSize: '1.15em'},
        color: theme.palette.secondary.main,
    },
    mainContainer: {
        minHeight: '600px',
        width: '100vw',
        marginTop: 20,
        position: 'relative',
        [theme.breakpoints.down('md')]: {padding: theme.spacing(0)},
    },
    coursePaper: {
        minHeight: '600px',
        width: "100vw",
        textAlign: 'center',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        padding: '24px 48px',
        [theme.breakpoints.down('md')]: {padding: '24px 36px'},
        [theme.breakpoints.down(1050)]: {padding: '24px 12px'},
        [theme.breakpoints.down('sm')]: {padding: '16px 48px'},
        [theme.breakpoints.down( 750)]: {padding: '16px 12px'},
        [theme.breakpoints.down('xs')]: {padding: '16px 8px'},
    }, 
    wrapper: {
        marginTop: 20,
        marginBottom: 10,
        position: 'relative',
    },
    buttonProgress: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '10%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    button: {
        backgroundColor: theme.palette.primary.main,
        '&:hover': {backgroundColor: theme.palette.primary.dark},
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 2,
    },
    courseListTitle: {
        color: theme.palette.secondary.main,
        letterSpacing: 2,
        fontWeight: 600,
        fontSize: '1.2rem',
        lineHeight: '1.5rem',
        width: '100%',
        textAlign: 'center',
        margin: '10px 0px 20px 0px',
    },
    
    paper: {
        minHeight: '300px',
        width: "100vw",
        textAlign: 'center',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        padding: '24px 48px',
        [theme.breakpoints.down('md')]: {padding: '24px 36px'},
        [theme.breakpoints.down(1050)]: {padding: '24px 12px'},
        [theme.breakpoints.down('sm')]: {padding: '16px 48px'},
        [theme.breakpoints.down( 750)]: {padding: '16px 12px'},
        [theme.breakpoints.down('xs')]: {padding: '16px 8px'},
    }, 
    blogsTitle: {
        color: theme.palette.secondary.main,
        letterSpacing: 2,
        fontWeight: 600,
        fontSize: '1.2rem',
        lineHeight: '1.5rem',
        width: '100%',
        textAlign: 'center',
        margin: '10px 0px 10px 0px',
        [theme.breakpoints.down(960)]: {margin: '10px 0px'}
    },
    blogs: {
        margin: '0px auto',
        justifyContent: 'center',
        maxWidth: '100%',
        minHeight: '100px',   
        [theme.breakpoints.down(960)]: {margin: '0px 0px'}               
    },    
    blogImage: {
        display: 'block',
        padding: '10px',
        height: '220px', width: '220px', 
        [theme.breakpoints.down(960)]: {height: '180px', width: '180px'},
        [theme.breakpoints.down(600)]: {height: '160px', width: '160px'},
        [theme.breakpoints.down(450)]: {height: '140px', width: '140px'},  
        [theme.breakpoints.down(390)]: {height: '130px', width: '130px'},
    },

    quoteTitleSection: {
        padding: 20,
        [theme.breakpoints.down('md')]: {padding: 20},
        [theme.breakpoints.down('sm')]: {padding: 10},
    },
    quoteTitle: {
        color: theme.palette.secondary.main,
        letterSpacing: 2,
        fontWeight: 600,
        fontSize: '1.2rem',
        lineHeight: '1.5rem',
    },
    quotes: {
        fontSize: '1.2rem',
        paddingBottom: '3rem',
        margin: '0 auto',
        width: '100%',
        [theme.breakpoints.up(801)]: {width: '70%'},
        [theme.breakpoints.up(960)]: {width: '70%'},
        [theme.breakpoints.up('md')]: {width: '60%'},
    }
}));

const initialQuotes = [
    {   image: 'kaiming', name: '江啟明', link: '/user/5',
        bio1: '學識教育平台負責人', bio2: '前香港中文大學高級講師', bio3: '新南威爾斯大學機電工程學博士', 
        quote: '「21世紀AI年代，教育唔應該只係喺學校或學院內發生。教育應該成為我哋日常生活一部分，讓我哋擁有更全面廣博嘅知識，才能與時並進。」',
    },
    {   image: 'willng', name: '吳偉賢', link: '/user/885',
        bio1: '劍橋大學工科博士', bio2: '民間學人', bio3: '',
        quote: '「香港制式教育幾十年來一個大問題，係文理分班，搞到人人甩甩漏漏，見樹不見林。做專才可能都足夠，想做通才就要識自學，識得補讀未完書。」',
    },
    {   image: 'meiyee', name: '梁美儀', link: '/user/3618',
        bio1: '前香港中文大學大學通識教育主任', bio2: '巴黎第一大學歷史學博士', bio3: '',
        quote: '「學識，不僅是獲得知識；更重要的是學會辨識。 懂得發問、探索、思考和判斷，這是學習者自身能力的提升。這個學習的歷程不能外判，也因此不容易被取代。」',
    },
    {   image: 'drlo', name: '盧傑雄', link: '/user/3605',
        bio1: '香港中文大學高級講師', bio2: '香港中文大學哲學博士', bio3: '香港實踐哲學學會創會會長',
        quote: '「學識廣，眼界濶；學識深，睿智增。」',        
    },
    {   image: 'bensir', name: '歐陽偉豪 Ben Sir', link: '/user/2608',
        bio1: '前香港中文大學高級講師', bio2: '香港科技大學語言學博士', bio3: '',
        quote: '「學識係少數平台以廣東話推廣普及學術。」',        
    },
    {   image: 'kimfan', name: '王劍凡', link: '/user/9',
        bio1: '旅行公民創辦人', bio2: '翻譯學博士，研究哲學翻譯', bio3: '',
        quote: '「學識是少數以廣東話為教學語言的網上學習平台，內容包羅萬有，從科學到人文，從理論到實踐，在今天這個知識愈來愈專精的世界，既可保育廣東話，亦可普及知識。」',            
    },
    {   image: 'chiwang', name: '陳志宏', link: '/user/2465',
        bio1: '普及科學教育工作者', bio2: '紐約大學物理學博士', bio3: '',
        quote: '「在這科技突飛猛進的時代，學習普及科學知識就是理想個人裝備。Hok6.com為你帶來真係好科普。」',        
    },
    {   image: 'chris', name: '李敏剛 Chris', link: '/user/1307',
        bio1: '香港恒生大學社會科學系助理教授', bio2: '中歐大學政治科學系博士', bio3: '',
        quote: '「學識提供了將世界學術轉化為在地的廣東話知識的新平台，不再需要局限在課室中愈來愈少的空間，為教師和學員打開了更多的可能。」',        
    },
    {   image: 'winsome', name: '李衍蒨 Winsome', link: '/user/1372',
        bio1: '法醫/生物人類學家', bio2: '任職於非政府組織和執法機構', bio3: '',
        quote: '「教育、學習唔應該分得太細，所有知識都可互通互用，學識集合你覺得有趣又層面廣的知識，讓我們透過學識學識以整體觀看世界！」',        
    },
    {   image: 'hugo', name: '黎智豐 Hugo', link: '/user/240',
        bio1: '先秦古代文獻研究者', bio2: '香港中文大學中國語言及文學博士', bio3: '',
        quote: '「教育係引導學生探索未知嘅過程。喺「學識」呢個跨越地域嘅母語學習空間，一齊發掘興趣、批判成見、連結生活係好值得珍惜嘅。」',        
    },
    {   image: 'laupo', name: '劉保禧', link: '/user/3722',
        bio1: '香港中文大學高級講師', bio2: '香港中文大學哲學博士', bio3: '',
        quote: '「有學識，識得學，學得識。」',       
    },
    {   image: 'karen', name: '麥嘉慧 Karen', link: '/user/893',
        bio1: '自由科普作家', bio2: '香港科技大學化學博士', bio3: '',
        quote: '「學識係一個用粵語解構宇宙、增潤各個人文範疇嘅萬花筒，學員可以用熟悉嘅語言解鎖無限知識，我大大力推！」',        
    },
    {   image: 'roger', name: '李敬恒', link: '/user/483',
        bio1: 'Roger 大師兄',bio2: '香港中文大學哲學博士', bio3: '哲學、文學節目主持及專欄作者',
        quote: '「學海無涯，識於微時。」',        
    },
    {   image: 'sabrina', name: '楊彩杰 Sabrina', link: '/user/13',
        bio1: '文學評論者', bio2: '巴黎索邦學院法國文學及比較文學博士', bio3: '',
        quote: '「有學識，可能不會令年少的創傷減輕或社會的問題減少，更可能是人生的煩惱還是會不多不少地迎面而來。但學識加上涵養可令一個人更有距離及層次地看待這些問題。」',       
    },
];

const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    adaptiveHeight: true,
};

const courseCarouselSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false, // We'll use custom arrows
    responsive: [
        {
            breakpoint: 1280,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 1,
            }
        },
        {
            breakpoint: 960,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
            }
        },
        {
            breakpoint: 600,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                rows: 2,
                slidesPerRow: 1,
            }
        }
    ]
};

export default function HomeScreen() {
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useDispatch();
    
    // Carousel navigation arrow component with access to classes
    // eslint-disable-next-line react/prop-types
    const CarouselArrow = ({ direction, onClick, disabled, appearEnabled }) => {
        const calculatedOpacity = (disabled && !appearEnabled) ? 0.3 : 1;
        return (
            <IconButton
                onClick={onClick}
                color="primary"
                aria-label={direction === 'prev' ? 'previous' : 'next'}
                size="small"
                disabled={disabled}
                className={appearEnabled ? classes.carouselArrowAppearEnabled : undefined}
                style={{
                    position: 'absolute',
                    [direction === 'prev' ? 'left' : 'right']: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.2)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    padding: '8px',
                    opacity: calculatedOpacity,
                }}
            >
                {direction === 'prev' ? <Icon.ChevronLeft fontSize="default" /> : <Icon.ChevronRight fontSize="default" />}
            </IconButton>
        );
    };

    const [initialLoadingCompleted, setInitialLoadingCompleted] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);

    const [displayCourses, setDisplayCourses] = useState([]);
    const [humanitiesCourses, setHumanitiesCourses] = useState([]);
    const [knowledgeCourses, setKnowledgeCourses] = useState([]);
    const [practicalCourses, setPracticalCourses] = useState([]);
    const [discountedCourses, setDiscountedCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingHumanities, setLoadingHumanities] = useState(true);
    const [loadingKnowledge, setLoadingKnowledge] = useState(true);
    const [loadingPractical, setLoadingPractical] = useState(true);
    const [loadingDiscounted, setLoadingDiscounted] = useState(true);
    const [loadPage, setLoadPage] = useState(1);
    const [hasMorePage, setHasMorePage] = useState(false);
    
    const [hasMoreHumanities, setHasMoreHumanities] = useState(false);
    const [hasMoreKnowledge, setHasMoreKnowledge] = useState(false);
    const [hasMorePractical, setHasMorePractical] = useState(false);
    const [hasMoreDiscounted, setHasMoreDiscounted] = useState(false);

    const [selectedSearchOptionId, setSelectedSearchOptionId] = useState(HOME_SCREEN_INITIAL_SEARCH_OPTION_ID);

    const mobileVersion = useMediaQuery(theme.breakpoints.down(840));

    const buttonClassname = clsx({
        [classes.button]: !loading,
    });

    const retrieveCourses = useCallback(async (optionId, page) => {
        try {
            const params = HomePageSearchOption[optionId].params;
            const ordering = HomePageSearchOption[optionId].ordering;
            const response = await dispatch(searchCourseGroup(params, page, ordering, HOMESCREEN_PAGE_SIZE));
            const hasMore = page < Math.ceil(response.count / HOMESCREEN_PAGE_SIZE);
            return [response.results, hasMore];
        } catch (e) {
            console.error("Failed to fetch courses:", e);
        }
        return [[], true];
    }, [dispatch]);

    const retrieveHumanitiesCourses = useCallback(async (page) => {
        try {
            const params = { category: ['PHILOSOPHY', 'HUMANITIES', 'ARTS', 'SPIRITUAL'] };
            const response = await dispatch(searchCourseGroup(params, page, CourseGroupOrdering.RECOMMENDED, HOMESCREEN_PAGE_SIZE));
            return [response.results, page < Math.ceil(response.count / HOMESCREEN_PAGE_SIZE)];
        } catch (e) {
            console.error("Failed to fetch humanities courses:", e);
        }
        return [[], false];
    }, [dispatch]);

    const retrieveKnowledgeCourses = useCallback(async (page) => {
        try {
            const params = { category: ['HISTORY', 'SOCIAL_SCIENCE', 'SCIENCE', 'CHILD'] };
            const response = await dispatch(searchCourseGroup(params, page, CourseGroupOrdering.RECOMMENDED, HOMESCREEN_PAGE_SIZE));
            return [response.results, page < Math.ceil(response.count / HOMESCREEN_PAGE_SIZE)];
        } catch (e) {
            console.error("Failed to fetch knowledge courses:", e);
        }
        return [[], false];
    }, [dispatch]);

    const retrievePracticalCourses = useCallback(async (page) => {
        try {
            const params = { category: ['TECHNOLOGY', 'BUSINESS', 'LIVING', 'HEALTH', 'LANGUAGE'] };
            const response = await dispatch(searchCourseGroup(params, page, CourseGroupOrdering.RECOMMENDED, HOMESCREEN_PAGE_SIZE));
            return [response.results, page < Math.ceil(response.count / HOMESCREEN_PAGE_SIZE)];
        } catch (e) {
            console.error("Failed to fetch practical courses:", e);
        }
        return [[], false];
    }, [dispatch]);

    const retrieveDiscountedCourses = useCallback(async (page) => {
        try {
            const params = { first_course_discount_applied: true };
            const response = await dispatch(searchCourseGroup(params, page, CourseGroupOrdering.RECOMMENDED, HOMESCREEN_PAGE_SIZE));
            return [response.results, page < Math.ceil(response.count / HOMESCREEN_PAGE_SIZE)];
        } catch (e) {
            console.error("Failed to fetch discounted courses:", e);
        }
        return [[], false];
    }, [dispatch]);

    const handleSearch = useCallback(
        async (event, optionId) => {
            if (event) {
                event.preventDefault();
            }
            setLoading(true);
            const optionChanged = selectedSearchOptionId !== optionId;
            const page = optionChanged ? 1 : loadPage + 1;
            const [courses, hasMore] = await retrieveCourses(optionId, page);
            
            setDisplayCourses(existingCourses => mergeArrayUnique(optionChanged ? [] : existingCourses, courses));
            setLoadPage(page);
            setSelectedSearchOptionId(optionId);
            setHasMorePage(hasMore);
            setLoading(false);
        },
        [setLoading, setSelectedSearchOptionId, retrieveCourses, selectedSearchOptionId, loadPage]
    );

    // Helper to build search URL with current filter
    const getSearchUrlForOption = useCallback((optionId) => {
        const option = HomePageSearchOption[optionId];
        const queryStr = queryString.stringify(
            {
                ...option.params,
                ordering: option.ordering?.sortingId || undefined,
            },
            { skipNull: true, arrayFormat: 'comma' }
        );
        return `/search?${queryStr}`;
    }, []);

    const getUrlByGroup = useCallback(group => getCourseGroupUrl(group.id), []);

    // Only fetch above-the-fold content (latest courses) for optimal LCP
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [courses, hasMoreCourses] = await retrieveCourses(HOME_SCREEN_INITIAL_SEARCH_OPTION_ID, 1);

                setDisplayCourses(courses);
                setHasMorePage(hasMoreCourses);                
                setInitialLoadingCompleted(true);
                setLoading(false);
                setFirstLoad(false);
                
            } catch (error) {
                console.error("Failed to fetch initial data:", error);
                setLoading(false);
            }
        };
        
        fetchInitialData();
        window.scrollTo(0, 0);

    }, [retrieveCourses, dispatch]);

    // Carousel sections ref for lazy loading
    const carouselsSectionRef = useRef(null);

    // Lazy load all 4 carousels in parallel when user scrolls near
    useEffect(() => {
        const fetchAllCarousels = async () => {
            try {
                const [
                    [humanitiesCourses, hasMoreHum],
                    [knowledgeCourses, hasMoreKnow],
                    [practicalCourses, hasMorePrac],
                    [discountedCourses, hasMoreDisc]
                ] = await Promise.all([
                    retrieveHumanitiesCourses(1),
                    retrieveKnowledgeCourses(1),
                    retrievePracticalCourses(1),
                    retrieveDiscountedCourses(1)
                ]);
            
                setHumanitiesCourses(humanitiesCourses);
                setKnowledgeCourses(knowledgeCourses);
                setPracticalCourses(practicalCourses);
                setDiscountedCourses(discountedCourses);
                setHasMoreHumanities(hasMoreHum);
                setHasMoreKnowledge(hasMoreKnow);
                setHasMorePractical(hasMorePrac);
                setHasMoreDiscounted(hasMoreDisc);
                setLoadingHumanities(false);
                setLoadingKnowledge(false);
                setLoadingPractical(false);
                setLoadingDiscounted(false);
            } catch (error) {
                console.error("Failed to fetch carousel courses:", error);
                setLoadingHumanities(false);
                setLoadingKnowledge(false);
                setLoadingPractical(false);
                setLoadingDiscounted(false);
            }
        };
        
        if (!carouselsSectionRef.current) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && humanitiesCourses.length === 0) {
                    fetchAllCarousels();
                }
            },
            { rootMargin: '800px' } // Start loading 800px before visible
        );
        
        observer.observe(carouselsSectionRef.current);
        return () => observer.disconnect();
    }, [retrieveHumanitiesCourses, retrieveKnowledgeCourses, retrievePracticalCourses, retrieveDiscountedCourses, humanitiesCourses.length]);

    // Humanities carousel state
    const humanitiesSliderRef = useRef(null);
    const [humanitiesCurrentSlide, setHumanitiesCurrentSlide] = useState(0);
    const [humanitiesIsEnd, setHumanitiesIsEnd] = useState(false);
    
    // Knowledge carousel state
    const knowledgeSliderRef = useRef(null);
    const [knowledgeCurrentSlide, setKnowledgeCurrentSlide] = useState(0);
    const [knowledgeIsEnd, setKnowledgeIsEnd] = useState(false);
    
    // Practical carousel state
    const practicalSliderRef = useRef(null);
    const [practicalCurrentSlide, setPracticalCurrentSlide] = useState(0);
    const [practicalIsEnd, setPracticalIsEnd] = useState(false);
    
    // Discounted carousel state
    const discountedSliderRef = useRef(null);
    const [discountedCurrentSlide, setDiscountedCurrentSlide] = useState(0);
    const [discountedIsEnd, setDiscountedIsEnd] = useState(false);

    // Helper function to get current carousel settings based on screen size
    const isMobile = useMediaQuery(theme.breakpoints.down(600));
    const isSmallTablet = useMediaQuery(theme.breakpoints.between(600, 960));
    
    const getCurrentCarouselSettings = useCallback(() => {
        if (isMobile) {
            return { slidesToShow: 1, rows: 2 };
        } else if (isSmallTablet) {
            return { slidesToShow: 2, rows: 1 };
        } else {
            return { slidesToShow: 3, rows: 1 };
        }
    }, [isMobile, isSmallTablet]);

    const checkIsAtEnd = useCallback((currentSlide, totalItems) => {
        const { slidesToShow, rows } = getCurrentCarouselSettings();
        const itemsPerSlide = slidesToShow * rows;
        
        let lastSlideIndex;
        if (rows === 2) {
            // Mobile: each slide contains itemsPerSlide items (2 in a 1×2 grid)
            lastSlideIndex = Math.floor(totalItems / itemsPerSlide) - 1;
        } else {
            // Tablet/Desktop: slides overlap, scrolls 1 item at a time
            lastSlideIndex = totalItems - slidesToShow;
        }
        
        return currentSlide >= lastSlideIndex;
    }, [getCurrentCarouselSettings]);

    // Initialize isEnd states when carousel data first loads
    useEffect(() => {
        if (humanitiesCourses.length > 0) {
            setHumanitiesIsEnd(checkIsAtEnd(0, humanitiesCourses.length));
        }
        if (knowledgeCourses.length > 0) {
            setKnowledgeIsEnd(checkIsAtEnd(0, knowledgeCourses.length));
        }
        if (practicalCourses.length > 0) {
            setPracticalIsEnd(checkIsAtEnd(0, practicalCourses.length));
        }
        if (discountedCourses.length > 0) {
            setDiscountedIsEnd(checkIsAtEnd(0, discountedCourses.length));
        }
    }, [humanitiesCourses.length, knowledgeCourses.length, practicalCourses.length, discountedCourses.length, checkIsAtEnd]);

    // shuffle blogs - Load only when user scrolls near (Intersection Observer)
    const [blogs, setBlogs] = useState([]);
    const blogSectionRef = useRef(null);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await dispatch(listBlogs(BLOG_LISTING_SEARCH_DEFAULT_PARAMS, 1));
                if (response && response.results) {
                    let filteredBlogs = response.results;
                    filteredBlogs = filteredBlogs.sort(() => Math.random() - 0.5);
                    filteredBlogs = filteredBlogs.slice(0, 10);
                    setBlogs(filteredBlogs);
                } else {
                    setBlogs([]);
                }
            } catch (error) {
                setBlogs([]);
            }
        };

        // Only load blogs when section is about to be visible (Intersection Observer)
        if (!blogSectionRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && blogs.length === 0) {
                    fetchBlogs();
                }
            },
            { rootMargin: '800px' } // Start loading 800px before visible
        );

        observer.observe(blogSectionRef.current);
        return () => observer.disconnect();
    }, [dispatch, blogs.length]);

    // Lazy load and shuffle quotes when user scrolls near
    const [shuffledQuotes, setShuffledQuotes] = useState([]);
    const quotesSectionRef = useRef(null);
    
    useEffect(() => {
        const shuffleQuotes = () => {
            const shuffleArray = (array) => {
                return array.sort(() => Math.random() - 0.5);
            };
            setShuffledQuotes(shuffleArray([...initialQuotes]));
        };
        
        if (!quotesSectionRef.current) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && shuffledQuotes.length === 0) {
                    shuffleQuotes();
                }
            },
            { rootMargin: '800px' }
        );
        
        observer.observe(quotesSectionRef.current);
        return () => observer.disconnect();
    }, [shuffledQuotes.length]);

    return (
    <>
      <MetaTags
            id='home'
            titles={['首頁']}
            description='學識 Hok6，專為香港人及全球廣東話使用者而設的網上教育平台，提供語言、文學、哲學、歷史、科技等優質線上課程及講座，讓你隨時隨地以粵語學習新知識。'
            image='/img/banner_socrates.jpg'
            imageWidth={1920}
            imageHeight={1080}
            url='/'
            prerenderReady={initialLoadingCompleted}
      />
      <StructuredData
            data={[
                buildOrganizationStructuredData(),
                buildWebsiteStructuredData(),
            ]}
      />

      <main>
        <Grid item className={classes.carouselContainer} container> 
            <Grid item xs={12} className={classes.carouselGrid}>
                <HomeCarousel/>
            </Grid>
        </Grid>

        <Grid item className={classes.headerHeight} container>
            <Grid item xs={mobileVersion ? 12:7} className={classes.headerTitleField}>
                <Typography variant="h1" component="h1" className={classes.headerTitle}>
                    穿越科技迷霧，學識迎接時代挑戰<br/>
                    開啟知識大門，創造未來無限可能
                </Typography>   
            </Grid>
            <Grid item xs={mobileVersion ? 12:5} className={classes.searchAndSelect}>
                <Grid className={classes.searchBar}>
                    <HomeSearchBar />
                </Grid>
                
            </Grid>
        </Grid>

        <Grid container item xs={12} className={classes.mainContainer}>
            {/* 最新課程/活動 */}
            <Card className={classes.coursePaper}>
                <Grid className={classes.selectButtonGroup}>
                    <ButtonGroup container disableElevation >
                        {_.map(HomePageSearchOption, (option, optionId) => (
                                <Button alignItems="center" key={optionId}  
                                    className={optionId === selectedSearchOptionId ? classes.selectedButton : classes.selectButton}
                                    selected={optionId === selectedSearchOptionId}
                                    onClick={event => handleSearch(event, optionId)}
                                >
                                    {option.displayName}
                                </Button>
                        ))}
                    </ButtonGroup>
                </Grid>
                {loading && !firstLoad &&(
                    <div className={classes.loadingOverlay}>
                        <CircularProgress size={50} />
                    </div>
                )}
                <Grid container spacing={2} style={{ minHeight: firstLoad ? '700px' : 'auto' }}>
                {_.map(displayCourses, courseGroup => (
                    <CourseCard
                        key={courseGroup.id}
                        target={courseGroup}
                        urlGenerator={getUrlByGroup}
                        md={4}
                    />
                ))}
                </Grid>
                <div className={classes.wrapper}>
                    {hasMorePage && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={buttonClassname}
                            component={Link}
                            to={getSearchUrlForOption(selectedSearchOptionId)}
                            endIcon={<Icon.ArrowForward />}
                        >
                            顯示更多
                        </Button>
                    )}
                </div>
            </Card>

            {/* 文學哲思 × 陶冶心靈 */}
            <Card ref={carouselsSectionRef} className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#F5F0FF',
                backgroundImage: 'linear-gradient(135deg, #F5F0FF 0%, #FAF5FF 100%)'
            }}>
                <Box position="relative">
                    <Link to={'/search?category=PHILOSOPHY,HUMANITIES,ARTS,SPIRITUAL&ordering=recommended'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.courseListTitle} style={{ color: '#7B5FA7' }}>
                            文學哲思 × 陶冶心靈
                        </Typography>
                    </Link>
                    {humanitiesCourses.length > 0 && !loadingHumanities && (
                        <>
                            <CarouselArrow
                                direction="prev"
                                onClick={() => humanitiesSliderRef.current?.slickPrev()}
                                disabled={humanitiesCurrentSlide === 0}
                            />
                            <CarouselArrow
                                direction="next"
                                onClick={() => humanitiesSliderRef.current?.slickNext()}
                                disabled={humanitiesIsEnd}
                                appearEnabled={hasMoreHumanities && humanitiesIsEnd}
                            />
                        </>
                    )}
                </Box>
                {loadingHumanities ? (
                    <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : humanitiesCourses.length > 0 ? (
                    <Slider
                        ref={humanitiesSliderRef}
                        {...courseCarouselSettings}
                        beforeChange={(current, next) => setHumanitiesCurrentSlide(next)}
                        afterChange={(current) => {setHumanitiesIsEnd(checkIsAtEnd(current, humanitiesCourses.length))}}
                    >
                        {_.map(humanitiesCourses, courseGroup => (
                            <Grid container spacing={2} key={courseGroup.id} style={{ padding: '0 8px' }}>
                                <CourseCard
                                    target={courseGroup}
                                    urlGenerator={getUrlByGroup}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                />
                            </Grid>
                        ))}
                    </Slider>
                ) : (
                    <Typography variant="body1" style={{ margin: '20px 0' }}>
                        暫時沒有相關課程
                    </Typography>
                )}
            </Card>

            {/* 古今中外 × 認識世界 */}
            <Card className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#EFF8F0',
                backgroundImage: 'linear-gradient(135deg, #EFF8F0 0%, #F7FDF8 100%)'
            }}>
                <Box position="relative">
                    <Link to={'/search?category=HISTORY,SOCIAL_SCIENCE,SCIENCE,CHILD&ordering=recommended'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.courseListTitle} style={{ color: '#4A9B5E' }}>
                            古今中外 × 認識世界
                        </Typography>
                    </Link>
                    {knowledgeCourses.length > 0 && !loadingKnowledge && (
                        <>
                            <CarouselArrow
                                direction="prev"
                                onClick={() => knowledgeSliderRef.current?.slickPrev()}
                                disabled={knowledgeCurrentSlide === 0}
                            />
                            <CarouselArrow
                                direction="next"
                                onClick={() => knowledgeSliderRef.current?.slickNext()}
                                disabled={knowledgeIsEnd}
                                appearEnabled={hasMoreKnowledge && knowledgeIsEnd}
                            />
                        </>
                    )}
                </Box>
                {loadingKnowledge ? (
                    <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : knowledgeCourses.length > 0 ? (
                    <Slider
                        ref={knowledgeSliderRef}
                        {...courseCarouselSettings}
                        beforeChange={(current, next) => setKnowledgeCurrentSlide(next)}
                        afterChange={(current) => {setKnowledgeIsEnd(checkIsAtEnd(current, knowledgeCourses.length))}}
                    >
                        {_.map(knowledgeCourses, courseGroup => (
                            <Grid container spacing={2} key={courseGroup.id} style={{ padding: '0 8px' }}>
                                <CourseCard
                                    target={courseGroup}
                                    urlGenerator={getUrlByGroup}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                />
                            </Grid>
                        ))}
                    </Slider>
                ) : (
                    <Typography variant="body1" style={{ margin: '20px 0' }}>
                        暫時沒有相關課程
                    </Typography>
                )}
            </Card>

            {/* 實用知識 × 升級生活 */}
            <Card className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#FFFAED',
                backgroundImage: 'linear-gradient(135deg, #FFFAED 0%, #FFFDF5 100%)'
            }}>
                <Box position="relative">
                    <Link to={'/search?category=TECHNOLOGY,BUSINESS,LIVING,HEALTH,LANGUAGE&ordering=recommended'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.courseListTitle} style={{ color: '#C9A547' }}>
                            實用知識 × 升級生活
                        </Typography>
                    </Link>
                    {practicalCourses.length > 0 && !loadingPractical && (
                        <>
                            <CarouselArrow
                                direction="prev"
                                onClick={() => practicalSliderRef.current?.slickPrev()}
                                disabled={practicalCurrentSlide === 0}
                            />
                            <CarouselArrow
                                direction="next"
                                onClick={() => practicalSliderRef.current?.slickNext()}
                                disabled={practicalIsEnd}
                                appearEnabled={hasMorePractical && practicalIsEnd}
                            />
                        </>
                    )}
                </Box>
                {loadingPractical ? (
                    <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : practicalCourses.length > 0 ? (
                    <Slider
                        ref={practicalSliderRef}
                        {...courseCarouselSettings}
                        beforeChange={(current, next) => setPracticalCurrentSlide(next)}
                        afterChange={(current) => {setPracticalIsEnd(checkIsAtEnd(current, practicalCourses.length))}}
                    >
                        {_.map(practicalCourses, courseGroup => (
                            <Grid container spacing={2} key={courseGroup.id} style={{ padding: '0 8px' }}>
                                <CourseCard
                                    target={courseGroup}
                                    urlGenerator={getUrlByGroup}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                />
                            </Grid>
                        ))}
                    </Slider>
                ) : (
                    <Typography variant="body1" style={{ margin: '20px 0' }}>
                        暫時沒有相關課程
                    </Typography>
                )}
            </Card>

            {/* 限時優惠 & 預售課程 */}
            <Card className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#FFEFED',
                backgroundImage: 'linear-gradient(135deg, #FFEFED 0%, #FFF7F5 100%)'
            }}>
                <Box position="relative">
                    <Link to={'/search?discount_applied=true&ordering=recommended'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.courseListTitle} style={{ color: '#E85745' }}>
                            限時優惠 & 預售課程
                        </Typography>
                    </Link>
                    {discountedCourses.length > 0 && !loadingDiscounted && (
                        <>
                            <CarouselArrow
                                direction="prev"
                                onClick={() => discountedSliderRef.current?.slickPrev()}
                                disabled={discountedCurrentSlide === 0}
                            />
                            <CarouselArrow
                                direction="next"
                                onClick={() => discountedSliderRef.current?.slickNext()}
                                disabled={discountedIsEnd}
                                appearEnabled={hasMoreDiscounted && discountedIsEnd}
                            />
                        </>
                    )}
                </Box>
                {loadingDiscounted ? (
                    <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : discountedCourses.length > 0 ? (
                    <Slider
                        ref={discountedSliderRef}
                        {...courseCarouselSettings}
                        beforeChange={(current, next) => setDiscountedCurrentSlide(next)}
                        afterChange={(current) => {setDiscountedIsEnd(checkIsAtEnd(current, discountedCourses.length))}}
                    >
                        {_.map(discountedCourses, courseGroup => (
                            <Grid container spacing={2} key={courseGroup.id} style={{ padding: '0 8px' }}>
                                <CourseCard
                                    target={courseGroup}
                                    urlGenerator={getUrlByGroup}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                />
                            </Grid>
                        ))}
                    </Slider>
                ) : (
                    <Typography variant="body1" style={{ margin: '20px 0' }}>
                        暫時沒有優惠課程/活動
                    </Typography>
                )}
            </Card>

            {/* 閱讀學識文章 */}
            <Card ref={blogSectionRef} className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#F0F5FB',
                backgroundImage: 'linear-gradient(135deg, #F0F5FB 0%, #F7FAFD 100%)'
            }}>
                <Grid item xs={12}>
                        {blogs.length > 0 &&
                            <Grid item xs={12}>
                                <Link to={'/blog'} style={{ textDecoration: 'none' }}>
                                    <Typography variant="h2" component="h2" className={classes.blogsTitle} margin="0 auto" style={{ color: '#5681AE' }}>
                                        閱讀學識文章
                                    </Typography>
                                </Link>
                                <Grid container item direction="row" className={classes.blogs}>
                                    {blogs.map(blog => (
                                        <Link key={blog.slug} to={getBlogDetailUrl(blog.slug)}>
                                            <img
                                                src={blog.coverImage}
                                                alt={blog.title}
                                                className={classes.blogImage}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </Link>
                                    ))}
                                </Grid>
                            </Grid>
                        }
                </Grid>
            </Card>

            {/* 關於學識 */}
            <Card ref={quotesSectionRef} className={classes.paper} style={{
                marginTop: '2rem',
                backgroundColor: '#FFF0F5',
                backgroundImage: 'linear-gradient(135deg, #FFF0F5 0%, #FFF7FA 100%)'
            }}>
                <Grid item xs={12} className={classes.quoteTitleSection}>
                    <Link to={'/about'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.quoteTitle} margin="0 auto" style={{ color: '#C75B8A' }}>
                            關於學識
                        </Typography>
                    </Link>
                </Grid>
                <Grid item xs={12} className={classes.quoteTitleSection}>
                    <Typography className={classes.quotes} margin="0 auto">時代巨輪急速轉動，「學識」係你思考同探索世界嘅橋樑。學識平台有各領域專家用廣東話，帶你穿越未來科技嘅迷霧、細味歷史智慧、感受哲學嘅樂趣、遊走文學與藝術之間。一齊喺知識世界，尋找屬於你嘅思考空間。</Typography>                    
                </Grid>
                <Grid item xs={12} className={classes.quoteTitleSection}>
                    <Link to={'/about#instructor-section'} style={{ textDecoration: 'none' }}>
                        <Typography variant="h2" component="h2" className={classes.quoteTitle} margin="0 auto" style={{ color: '#C75B8A' }}>
                            導師的話
                        </Typography>
                    </Link>
                </Grid>
                {shuffledQuotes.length > 0 && (
                    <Grid item xs={12} className={classes.quotes}>
                        <Slider {...settings}>
                            {shuffledQuotes.slice(0, 10).map((quote, index) => (
                                <QuoteItem key={index} {...quote} />
                            ))}
                        </Slider>
                    </Grid>
                )}
            </Card>
        </Grid>
      </main>
    </>);
}
